import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import MicIcon from '@mui/icons-material/Mic';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';

import { useAvatar, AVATAR_STATUS } from './AvatarContext';
import SubscaleQuiz from './Components/SubscaleQuiz';
import config from './config';
import { getTalkingHeadVoiceConfig } from './voicePreferences';
import {
  HABC_SECTIONS,
  CATEGORY_TO_SECTION_INDEX,
  SUBSCALE_LABELS,
  HABC_FULL_CATEGORY,
  isQuizCategory,
  buildQuestionsByKey,
} from './habcSubscales';
import './App.css';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const CRISIS_MODAL_CONTENT = {
  intro: [
    "I am really sorry you are feeling this way, but I am so glad you reached out. You don't have to carry this weight alone, and there are people who want to support you through this right now.",
    'Please consider reaching out to a crisis professional. They are available 24/7, they are free, and they provide a safe, confidential space to talk.',
  ],
  resources: [
    {
      label: '988 Suicide & Crisis Lifeline (USA & Canada)',
      action: 'Call or text',
      number: '988',
    },
    {
      label: 'Crisis Text Line',
      action: 'Text HOME to',
      number: '741741',
    },
    {
      label: 'The Trevor Project (LGBTQ+ Youth)',
      action: 'Call',
      number: '1-866-488-7386',
      secondaryAction: 'or text START to',
      secondaryNumber: '678-678',
    },
  ],
  nextSteps: [
    'If you are in immediate danger: Please call 911 (or your local emergency services) or go to the nearest emergency room.',
    'Reach out to a trusted person: Call a friend, a family member, or a mentor and tell them you are struggling.',
    'Create a safe space: Remove any items from your immediate area that you could use to hurt yourself.',
    'Please hang in there. Things can get better, and talking to someone is a brave and vital first step.',
  ],
};

const getZoneChipClass = (color) => {
  if (color === 'red') return 'bg-[#fde8e8] text-[#9b2424] border-[#f3b8b8]';
  if (color === 'orange') return 'bg-[#fff0dc] text-[#9a5800] border-[#f2c180]';
  if (color === 'yellow') return 'bg-[#fff8d7] text-[#7a6100] border-[#eadb82]';
  return 'bg-[#e5f6ea] text-[#1f7a3b] border-[#b8e0c3]';
};

const getPhoneHref = (phoneNumber) => {
  const cleanNumber = String(phoneNumber || '').replace(/[^\d+]/g, '');
  if (typeof navigator !== 'undefined' && navigator.platform?.toUpperCase().includes('MAC')) {
    return `facetime-audio://${cleanNumber}`;
  }

  return `tel:${cleanNumber}`;
};

const normalizeSessionConversation = (conversationData) => {
  const entries = Array.isArray(conversationData)
    ? conversationData.map((message, index) => [String(message?.timestamp || message?.createdAt || index), message])
    : Object.entries(conversationData || {});

  return entries
    .sort(([firstKey, firstMessage], [secondKey, secondMessage]) => {
      const firstSortValue = Number(firstMessage?.timestamp || firstKey);
      const secondSortValue = Number(secondMessage?.timestamp || secondKey);

      if (Number.isFinite(firstSortValue) && Number.isFinite(secondSortValue)) {
        return firstSortValue - secondSortValue;
      }

      return String(firstMessage?.createdAt || firstKey).localeCompare(
        String(secondMessage?.createdAt || secondKey)
      );
    })
    .flatMap(([timeKey, message]) => {
      if (!message || typeof message !== 'object') {
        return [];
      }

      const fallbackDate = Number.isFinite(Number(timeKey))
        ? new Date(Number(timeKey)).toISOString()
        : new Date().toISOString();
      const createdAt = message.createdAt || fallbackDate;
      const messages = [];
      const userText = message.user || message.userMessage;
      const botText = message.bot || message.agent || message.assistant;

      if (message.role && (message.text || message.content)) {
        messages.push({
          role: message.role === 'model' || message.role === 'agent' || message.role === 'assistant'
            ? 'model'
            : 'user',
          text: message.text || message.content,
          createdAt,
          evidenceBased: message.botMetadata?.evidenceBased,
          advisoryTag: message.botMetadata?.advisoryTag,
        });
        return messages;
      }

      if (userText) {
        messages.push({
          role: 'user',
          text: userText,
          createdAt,
        });
      }

      if (botText) {
        const meta = message.botMetadata || {};
        const botMessage = {
          role: 'model',
          text: botText,
          createdAt,
          evidenceBased: meta.evidenceBased,
          advisoryTag: meta.advisoryTag,
        };

        // Rebuild the subscale result (score chip + suggestion cards) that the
        // live flow attaches to this message, so it survives a reload.
        if (meta.stage === 'subscaleResult' && meta.category) {
          botMessage.subscaleResult = {
            category: meta.category,
            score: meta.score,
            maxScore: meta.maxScore,
            zoneLabel: meta.zoneLabel,
            zoneColor: meta.zoneColor,
            subscales: Array.isArray(meta.subscales) ? meta.subscales : [],
            suggestions: Array.isArray(meta.suggestions) ? meta.suggestions : [],
          };
          if (meta.suggestionChosen) {
            botMessage.suggestionChosen = meta.suggestionChosen;
          }
        }

        messages.push(botMessage);
      }

      return messages;
    });
};

// Fixed session opener. Keep in sync with buildSessionGreeting in backend
// app.js; /sessiongreeting persists it as the session's first conversation
// entry. The name follows the selected avatar (avatarCatalog.js).
const buildSessionGreeting = (assistantName) =>
  `Hello there! I'm so glad you reached out today. I'm ${assistantName}, and I'm here to support you on your caregiving journey. How can I assist you today?`;

const getSessionConversationData = (sessionData = {}) => {
  const conversationData = { ...(sessionData.conversation || {}) };

  Object.entries(sessionData).forEach(([key, value]) => {
    if (key.startsWith('conversation.') && value && typeof value === 'object') {
      const [, timestamp] = key.split('.');
      conversationData[timestamp || value.timestamp || key] = value;
    }
  });

  return conversationData;
};

// Voice input: keep listening through natural pauses and send once the
// caregiver has been silent this long (the browser's own end-of-speech
// detection cuts people off after ~1s).
const SPEECH_SILENCE_SEND_MS = 2500;
// Give up if nothing at all is heard after the mic is opened.
const SPEECH_NO_INPUT_TIMEOUT_MS = 10000;
const MIC_IDLE_HINT = 'Tap the mic and speak. It sends when you pause.';

function Agent() {
  const initialSessionId = localStorage.getItem('sessionId');
  const initialSessionIsNew = localStorage.getItem('sessionIsNew') === 'true';
  const [conversation, setConversation] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [statusMessage, setStatusMessage] = useState(MIC_IDLE_HINT);
  const [error, setError] = useState('');
  const [textInput, setTextInput] = useState('');
  const [sessionStarted, setSessionStarted] = useState(Boolean(initialSessionId) && !initialSessionIsNew);
  const [isSessionLoading, setIsSessionLoading] = useState(Boolean(initialSessionId));
  const [speechRecognitionSupported, setSpeechRecognitionSupported] = useState(false);
  const [crisisModalOpen, setCrisisModalOpen] = useState(false);
  // Which subscale quiz is open in the chat (null = closed).
  const [quizCategory, setQuizCategory] = useState(null);

  const url = `${config.API_BASE_URL}/api/chat`;
  const recognition = useRef(null);
  const silenceTimerRef = useRef(null);
  const speechTranscriptRef = useRef('');
  const speechDoneRef = useRef(true);
  const isListeningRef = useRef(false);
  // Timers fire from closures created when listening started; always send via
  // the latest function so the message joins the current conversation.
  const sendMessageRef = useRef(null);
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);
  const conversationEndRef = useRef(null);
  const {
    speakText: avatarSpeak,
    stopSpeaking: avatarStop,
    setStatus: setAvatarStatus,
    status: avatarStatus,
    muted,
    setMuted,
    assistantName,
  } = useAvatar();
  // Read from effects and async handlers without re-running them.
  const assistantNameRef = useRef(assistantName);
  assistantNameRef.current = assistantName;
  const navigate = useNavigate();
  const startListeningRef = useRef(null);
  const prevAvatarStatusRef = useRef(avatarStatus);

  useEffect(() => {
    setSpeechRecognitionSupported(!!SpeechRecognition);

    if (!SpeechRecognition) {
      setError('Speech Recognition not supported in this browser.');
    }
  }, []);

  const requestMicrophonePermission = useCallback(async () => {
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      return true;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      console.error('Microphone permission request failed:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const sessionId = localStorage.getItem('sessionId');

    if (!userId || !sessionId) {
      setIsSessionLoading(false);
      return;
    }

    const sessionIsNew = localStorage.getItem('sessionIsNew') === 'true';
    setIsSessionLoading(true);

    axios
      .get(`${config.API_BASE_URL}/getsession`, {
        params: { userId, sessionId },
      })
      .then((res) => {
        const convArr = normalizeSessionConversation(getSessionConversationData(res.data));

        // New sessions store the opener via /sessiongreeting, so it normally
        // loads with the transcript. Restore it whenever it's missing: older
        // sessions saved before the opener was persisted, or a session whose
        // greeting write failed (e.g. backend unreachable at the time).
        const startsWithGreeting = convArr.length && convArr[0].role === 'model';
        if (!startsWithGreeting && (convArr.length || !sessionIsNew)) {
          convArr.unshift({
            role: 'model',
            text: buildSessionGreeting(assistantNameRef.current),
            createdAt: convArr[0]?.createdAt || new Date().toISOString(),
            evidenceBased: true,
          });
        }

        setConversation(convArr);
        if (convArr.length) {
          setSessionStarted(true);
          localStorage.setItem('sessionIsNew', 'false');
        }
      })
      .catch((err) => {
        console.error('Error fetching session:', err);
        if (!sessionIsNew) {
          // Still show the opener so a reopened session never looks empty.
          setConversation([
            {
              role: 'model',
              text: buildSessionGreeting(assistantNameRef.current),
              createdAt: new Date().toISOString(),
              evidenceBased: true,
            },
          ]);
          setSessionStarted(true);
        }
      })
      .finally(() => {
        setIsSessionLoading(false);
      });
  }, []);

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const speakText = useCallback(
    (text) => {
      if (!text) {
        return;
      }

      if (!avatarSpeak(text)) {
        setStatusMessage('Voice output unavailable on this device.');
        return;
      }

      setIsSpeaking(true);
      setStatusMessage('Avatar speaking...');

      setTimeout(() => {
        setIsSpeaking(false);
        setStatusMessage(isListeningRef.current ? 'Listening...' : MIC_IDLE_HINT);
      }, 2000);
    },
    [avatarSpeak]
  );

  const stopSpeaking = useCallback(() => {
    avatarStop();
    setIsSpeaking(false);
  }, [avatarStop]);

  const buildHistoryPayload = (messages) => {
    return messages.slice(-12).map((msg) => ({
      role: msg.role === 'model' ? 'agent' : 'user',
      content: msg.text,
    }));
  };

  const sendMessageToBackend = async (userText) => {
    if (!userText?.trim()) {
      return;
    }

    const cleanText = userText.trim();
    setSessionStarted(true);
    setIsListening(false);
    recognition.current?.stop();
    setStatusMessage('Thinking...');
    setAvatarStatus(AVATAR_STATUS.thinking);
    setError('');

    const newUserMessage = {
      role: 'user',
      text: cleanText,
      createdAt: new Date().toISOString(),
    };

    const updatedConversation = [...conversation, newUserMessage];
    setConversation(updatedConversation);

    try {
      const response = await axios.post(url, {
        userMessage: cleanText,
        sessionId: localStorage.getItem('sessionId'),
        userId: localStorage.getItem('userId'),
        userEmail: localStorage.getItem('authEmail'),
        history: buildHistoryPayload(updatedConversation),
        assistantName: assistantNameRef.current,
      });

      const { text, evidenceBased, advisoryTag, stage, category } = response.data;
      if (!text) {
        setError(response.data.details || 'Received empty response from server.');
        setStatusMessage('Response error');
        return;
      }

      if (text.trim() === '+18126715565') {
        setCrisisModalOpen(true);
        setStatusMessage('Support resources available');
        return;
      }

      const modelResponse = {
        role: 'model',
        text,
        createdAt: new Date().toISOString(),
        evidenceBased,
        advisoryTag,
        stage,
        category,
        inviteHandled: false,
      };

      setConversation((prev) => [...prev, modelResponse]);

      if (!response.data.error) {
        speakText(text);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(
        `Failed to get response: ${
          err.response?.data?.error || err.message || 'Network error'
        }`
      );
      setStatusMessage('Error communicating');
      setAvatarStatus(AVATAR_STATUS.idle);
    } finally {
      if (!isSpeaking && !error) {
        setStatusMessage(MIC_IDLE_HINT);
      }
    }
  };
  sendMessageRef.current = sendMessageToBackend;

  const clearSilenceTimer = () => {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
  };

  // Ends the current voice turn exactly once. `send` false = cancelled.
  const finishSpeech = (send) => {
    clearSilenceTimer();
    if (speechDoneRef.current) {
      return;
    }
    speechDoneRef.current = true;

    const text = speechTranscriptRef.current.trim();
    speechTranscriptRef.current = '';
    // abort(), not stop(): stop() still delivers results for audio captured
    // so far, which would land in the input box after this turn was sent.
    try {
      recognition.current?.abort();
    } catch {
      // already stopped
    }
    setIsListening(false);
    setTextInput('');

    if (send && text) {
      // sendMessageToBackend switches the status to thinking.
      sendMessageRef.current?.(text);
    } else if (send) {
      setAvatarStatus(AVATAR_STATUS.idle);
      setStatusMessage('Did not hear anything. Tap the mic to try again.');
    } else {
      setAvatarStatus(AVATAR_STATUS.idle);
      setStatusMessage(MIC_IDLE_HINT);
    }
  };

  const armSilenceTimer = (ms) => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => finishSpeech(true), ms);
  };

  const handleToggleListen = async () => {
    if (!speechRecognitionSupported || isSpeaking) {
      return;
    }

    // Tapping while listening cancels this voice turn without sending.
    if (isListening) {
      finishSpeech(false);
      return;
    }

    // A deliberate tap always works, so it also lifts a mute.
    if (muted) {
      setMuted(false);
    }
    await startListening();
  };

  // Opens the mic for one voice turn. Called by the button and, hands-free,
  // after the avatar finishes speaking.
  const startListening = async () => {
    if (!speechRecognitionSupported || isListeningRef.current) {
      return;
    }

    const microphoneAllowed = await requestMicrophonePermission();
    if (!microphoneAllowed) {
      setError(
        'Microphone access denied. Enable mic permission in Android Settings > Apps > VICA > Permissions.'
      );
      setStatusMessage('Mic permission required');
      return;
    }

    speechTranscriptRef.current = '';
    speechDoneRef.current = false;

    recognition.current = new SpeechRecognition();
    recognition.current.continuous = true;
    recognition.current.interimResults = true;
    recognition.current.lang = getTalkingHeadVoiceConfig().speechLang;

    recognition.current.onstart = () => {
      setIsListening(true);
      setAvatarStatus(AVATAR_STATUS.listening);
      setStatusMessage('Listening... it sends when you pause.');
      setError('');
      // Don't let the mic pick up the avatar's own voice.
      stopSpeaking();
      armSilenceTimer(SPEECH_NO_INPUT_TIMEOUT_MS);
    };

    recognition.current.onresult = (event) => {
      if (speechDoneRef.current) {
        return;
      }
      const segments = [];
      for (let i = 0; i < event.results.length; i += 1) {
        segments.push(event.results[i][0].transcript.trim());
      }
      const combined = segments.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
      speechTranscriptRef.current = combined;
      // Show the words live so the caregiver can see what was heard.
      setTextInput(combined);
      armSilenceTimer(SPEECH_SILENCE_SEND_MS);
    };

    recognition.current.onerror = (event) => {
      if (event.error === 'aborted') {
        return;
      }
      if (event.error === 'no-speech') {
        finishSpeech(true);
        return;
      }
      console.error('SpeechRecognition Error:', event.error);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError(
          'Microphone access is blocked. Enable mic permission in Android Settings and try again.'
        );
        setStatusMessage('Mic permission required');
      } else {
        setError(`Speech recognition error: ${event.error}`);
        setStatusMessage('Recognition error');
      }
      speechDoneRef.current = true;
      clearSilenceTimer();
      setIsListening(false);
      setAvatarStatus(AVATAR_STATUS.idle);
    };

    // The browser can end a continuous session on its own (network hiccup,
    // time limit); send whatever was captured rather than losing it.
    recognition.current.onend = () => {
      finishSpeech(true);
    };

    try {
      recognition.current.start();
    } catch (err) {
      console.error('Error starting recognition:', err);
      speechDoneRef.current = true;
      setError(`Could not start listening: ${err.message}`);
      setIsListening(false);
      setStatusMessage('Mic start error');
    }
  };

  startListeningRef.current = startListening;

  // Hands-free: when the avatar finishes speaking on the chat page, open the
  // mic so the caregiver can answer without tapping (unless muted or a
  // subscale card is open, where she is reading questions, not asking).
  useEffect(() => {
    const previous = prevAvatarStatusRef.current;
    prevAvatarStatusRef.current = avatarStatus;
    if (
      previous === AVATAR_STATUS.speaking &&
      avatarStatus === AVATAR_STATUS.idle &&
      !muted &&
      !quizCategory &&
      sessionStarted
    ) {
      startListeningRef.current?.();
    }
  }, [avatarStatus, muted, quizCategory, sessionStarted]);

  // Muting while listening stops the current voice turn without sending.
  useEffect(() => {
    if (muted && isListeningRef.current) {
      finishSpeech(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  useEffect(() => {
    return () => {
      clearTimeout(silenceTimerRef.current);
      speechDoneRef.current = true;
      try {
        recognition.current?.abort();
      } catch {
        // not running
      }
    };
  }, []);

  // Show and speak the fixed opener immediately; persisting it to the session
  // record happens in the background (no LLM call involved).
  const handleStartSession = () => {
    setSessionStarted(true);
    localStorage.setItem('sessionIsNew', 'false');
    setError('');

    const greeting = buildSessionGreeting(assistantName);
    setConversation([
      {
        role: 'model',
        text: greeting,
        createdAt: new Date().toISOString(),
        evidenceBased: true,
      },
    ]);
    speakText(greeting);

    axios
      .post(`${config.API_BASE_URL}/sessiongreeting`, {
        userId: localStorage.getItem('userId'),
        sessionId: localStorage.getItem('sessionId'),
        assistantName,
      })
      .catch((err) => {
        console.error('Error saving session greeting:', err);
      });
  };

  const formatDateHeader = (isoDate) => {
    const date = new Date(isoDate);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    }

    if (diffDays === 1) {
      return 'Yesterday';
    }

    if (diffDays < 7) {
      return date.toLocaleDateString(undefined, { weekday: 'long' });
    }

    return date.toLocaleDateString(undefined, {
      year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Caregiver clicked Yes/Maybe-later on the HABC subscale invitation.
  const handleInviteResponse = useCallback((messageIndex, accepted, category) => {
    const replyText = accepted ? "Yes, I'm willing to complete it." : 'Maybe later.';

    setConversation((prev) => {
      const next = prev.map((msg, idx) =>
        idx === messageIndex ? { ...msg, inviteHandled: true } : msg
      );
      next.push({
        role: 'user',
        text: replyText,
        createdAt: new Date().toISOString(),
      });
      return next;
    });

    if (accepted) {
      setQuizCategory(category);
    }
  }, []);

  // Caregiver picked one suggestion to focus on for the next two weeks.
  const handleSuggestionChoice = useCallback((messageIndex, suggestion, subscaleResult) => {
    const choiceLabel = suggestion.title || suggestion.advice;
    const ackText = `Great choice! Over the next two weeks, try this: ${suggestion.advice} I'll check in with you on how it goes — and feel free to ask me anytime if you run into trouble.`;
    const choiceText = `I'd like to start with: ${choiceLabel}`;

    // Persist the chosen focus so it shows on the home page and can be
    // followed up in later sessions.
    axios
      .post(`${config.API_BASE_URL}/api/problems`, {
        userId: localStorage.getItem('userId'),
        sessionId: localStorage.getItem('sessionId'),
        category: subscaleResult?.category,
        suggestion,
        allSuggestions: subscaleResult?.suggestions || [],
        score: subscaleResult?.score,
        maxScore: subscaleResult?.maxScore,
        zoneLabel: subscaleResult?.zoneLabel,
        zoneColor: subscaleResult?.zoneColor,
        choiceText,
        ackText,
      })
      .catch((err) => console.error('Failed to save chosen focus:', err));

    setConversation((prev) => {
      const next = prev.map((msg, idx) =>
        idx === messageIndex
          ? { ...msg, suggestionChosen: suggestion.sourceId || choiceLabel }
          : msg
      );
      next.push({
        role: 'user',
        text: choiceText,
        createdAt: new Date().toISOString(),
      });
      next.push({
        role: 'model',
        text: ackText,
        createdAt: new Date().toISOString(),
      });
      return next;
    });

    speakText(ackText);
  }, [speakText]);

  // All subscale questions answered: submit to backend for scoring, handout
  // retrieval, and LLM interpretation, then post the result into the chat.
  const handleQuizComplete = useCallback(async (answers) => {
    const category = quizCategory;
    const label = SUBSCALE_LABELS[category] || 'subscale';
    const sectionIndex = CATEGORY_TO_SECTION_INDEX[category];

    setQuizCategory(null);
    setStatusMessage('Analyzing your answers...');
    setError('');

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/subscale-complete`, {
        userId: localStorage.getItem('userId'),
        sessionId: localStorage.getItem('sessionId'),
        userEmail: localStorage.getItem('authEmail'),
        category,
        answers,
        questions: HABC_SECTIONS[sectionIndex]?.items || [],
        questionsByKey: buildQuestionsByKey(category),
        history: buildHistoryPayload(conversation),
        assistantName: assistantNameRef.current,
      });

      const { text, score, maxScore, zone, suggestions, subscales } = response.data;
      const resultText = text || `Thank you for completing the ${label} questions.`;

      setConversation((prev) => [
        ...prev,
        {
          role: 'model',
          text: resultText,
          createdAt: new Date().toISOString(),
          subscaleResult: {
            category,
            score,
            maxScore,
            zoneLabel: zone?.label,
            zoneColor: zone?.color,
            subscales: Array.isArray(subscales) ? subscales : [],
            suggestions: suggestions || [],
          },
        },
      ]);
      speakText(resultText);
    } catch (err) {
      console.error('Subscale submit error:', err);
      setError(`Failed to process your answers: ${err.response?.data?.error || err.message}`);
      setConversation((prev) => [
        ...prev,
        {
          role: 'model',
          text: `Thank you for completing the ${label} questions. I could not process the results right now — please try again in a moment.`,
          createdAt: new Date().toISOString(),
        },
      ]);
      setStatusMessage(MIC_IDLE_HINT);
    }
  }, [quizCategory, conversation, speakText]);

  const renderedConversation = useMemo(() => {
    let lastDateHeader = null;

    return conversation.map((msg, index) => {
      const currentHeader = formatDateHeader(msg.createdAt);
      const showHeader = currentHeader !== lastDateHeader;
      if (showHeader) {
        lastDateHeader = currentHeader;
      }

      return (
        <React.Fragment key={`msg-${index}`}>
          {showHeader && <div className="vica-date-divider">{currentHeader}</div>}
          <div className={`vica-message-row ${msg.role === 'user' ? 'user' : 'model'}`}>
            <div className={`vica-message-bubble ${msg.role === 'user' ? 'user' : 'model'}`}>
              <div className="text-xl font-semibold mb-1">
                {msg.role === 'user' ? 'You' : 'Avatar'}
              </div>
              <div>{msg.text}</div>
              <div className="vica-message-meta">{formatTime(msg.createdAt)}</div>
              {msg.role === 'model' && msg.evidenceBased === false && (
                <div className="mt-2">
                  <span className="vica-tag vica-tag-warning">
                    {msg.advisoryTag || 'Not evidence-based from ABC protocol JSON'}
                  </span>
                </div>
              )}
              {msg.role === 'model' && msg.subscaleResult && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className={`vica-tag border !text-[1.1rem] !px-3.5 !py-1.5 ${getZoneChipClass(msg.subscaleResult.zoneColor)}`}>
                    {SUBSCALE_LABELS[msg.subscaleResult.category] || 'Subscale'}: {msg.subscaleResult.score}/{msg.subscaleResult.maxScore} · {msg.subscaleResult.zoneLabel}
                  </span>
                  {(msg.subscaleResult.subscales || []).map((subscale) => (
                    <span
                      key={subscale.category}
                      className={`vica-tag border !text-[1.1rem] !px-3.5 !py-1.5 ${getZoneChipClass(subscale.zoneColor)}`}
                    >
                      {subscale.label || SUBSCALE_LABELS[subscale.category]}: {subscale.score}/{subscale.maxScore}
                    </span>
                  ))}
                </div>
              )}
              {msg.role === 'model' &&
                (msg.subscaleResult?.suggestions?.length || 0) > 0 &&
                !msg.suggestionChosen && (
                  <div className="mt-4 space-y-3">
                    <div className="text-[1.15rem] font-extrabold uppercase tracking-wide text-[#3f6173]">
                      Choose one to focus on for the next two weeks:
                    </div>
                    {msg.subscaleResult.suggestions.map((suggestion, suggestionIndex) => (
                      <button
                        key={suggestion.sourceId || suggestionIndex}
                        type="button"
                        className="block w-full rounded-2xl border border-[#b7ddf1] bg-[#f0f9ff] px-5 py-4 text-left transition hover:bg-[#dff0fc]"
                        onClick={() => handleSuggestionChoice(index, suggestion, msg.subscaleResult)}
                      >
                        <span className="block text-[1.4rem] leading-9 font-extrabold text-[#0f4e71]">
                          {suggestionIndex + 1}. {suggestion.title || suggestion.advice}
                        </span>
                        {suggestion.title && (
                          <span className="mt-1 block text-[1.2rem] font-semibold leading-8 text-[#4f6f80]">
                            {suggestion.advice}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              {msg.role === 'model' &&
                msg.stage === 'classified' &&
                isQuizCategory(msg.category) &&
                !msg.inviteHandled &&
                index === conversation.length - 1 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="vica-btn vica-btn-lg text-xl"
                      onClick={() => handleInviteResponse(index, true, msg.category)}
                    >
                      Yes, I&apos;m willing (~{msg.category === HABC_FULL_CATEGORY ? 10 : 5} min)
                    </button>
                    <button
                      type="button"
                      className="vica-btn-secondary vica-btn-lg text-xl"
                      onClick={() => handleInviteResponse(index, false, msg.category)}
                    >
                      Maybe later
                    </button>
                  </div>
                )}
            </div>
          </div>
        </React.Fragment>
      );
    });
  }, [conversation, handleInviteResponse, handleSuggestionChoice]);

  // The app shell supplies the background, the avatar column and this panel's
  // column; the chat page only renders what goes inside that column.
  return (
    <>
        {/* While a subscale is being answered, its card takes the chat panel's
            place so the avatar stays fully visible beside it (no backdrop). */}
        {quizCategory ? (
          <SubscaleQuiz
            category={quizCategory}
            onClose={() => setQuizCategory(null)}
            onComplete={handleQuizComplete}
            speakText={speakText}
            stopSpeaking={stopSpeaking}
          />
        ) : (
        <div className="vica-surface vica-agent-panel">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="m-0 text-[2.2rem] font-extrabold text-[#0f3950]">Live Care Assistant</h2>
              <p className="m-0 mt-1 text-[1.3rem] leading-8 text-[#4f6f80]">Ask a question by voice or text.</p>
            </div>
            <button
              className="vica-btn-secondary vica-session-btn-new shrink-0"
              onClick={() => {
                stopSpeaking();
                navigate('/home');
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 28 }} />
              Back
            </button>
          </div>

          <div className="vica-chat-window vica-scrollbar">
            {conversation.length === 0 && sessionStarted && !isSessionLoading ? (
              <div className="h-full flex items-center justify-center text-center px-4 text-sm font-semibold text-[#4f6f80]">
                No saved messages were found for this session yet. Continue below and new messages will be saved here.
              </div>
            ) : (
              renderedConversation
            )}
            <div ref={conversationEndRef} />
          </div>

          <div className="mt-3">
            {isSessionLoading ? (
              <div className="text-sm text-[#3f6173] text-center font-semibold py-3">
                Loading session...
              </div>
            ) : conversation.length === 0 && !sessionStarted ? (
              <button className="vica-btn vica-btn-lg w-full" onClick={handleStartSession}>
                Start Session
              </button>
            ) : (
              <>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    className="vica-input text-xl"
                    placeholder="Type your message..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && textInput.trim()) {
                        sendMessageToBackend(textInput);
                        setTextInput('');
                      }
                    }}
                  />

                  <button
                    className="vica-btn vica-btn-md"
                    onClick={() => {
                      if (textInput.trim()) {
                        sendMessageToBackend(textInput);
                        setTextInput('');
                      }
                    }}
                    disabled={!textInput.trim() || isSpeaking}
                    title="Send"
                  >
                    <SendIcon />
                  </button>

                </div>

                <div className="mt-3 flex justify-center">
                  <button
                    type="button"
                    className={`vica-mic-button ${isListening ? 'is-listening' : ''}`}
                    onClick={handleToggleListen}
                    disabled={isSpeaking || !speechRecognitionSupported}
                    aria-pressed={isListening}
                    title={isListening ? 'Tap to cancel' : 'Tap to talk'}
                  >
                    <MicIcon style={{ fontSize: 30 }} />
                    <span>{isListening ? 'Listening...' : 'Tap to talk'}</span>
                  </button>
                </div>

                <div className="mt-2 text-base text-[#3f6173] text-center font-semibold">{statusMessage}</div>
                {error && <div className="text-red-700 mt-1 text-center text-base font-semibold">{error}</div>}
              </>
            )}
          </div>
        </div>
        )}

      {crisisModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f2b3a]/55 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crisis-support-title"
        >
          <div className="relative w-full md:w-[80vw] max-w-5xl max-h-[90vh] overflow-y-auto rounded-[18px] bg-white shadow-[0_24px_70px_rgba(0,52,80,0.28)] border border-[#cfe2ee]">
            <button
              type="button"
              className="absolute right-4 top-4 h-9 w-9 rounded-full border border-[#cfe2ee] bg-[#f5fbff] text-[#0f3950] text-xl leading-none font-bold"
              onClick={() => setCrisisModalOpen(false)}
              aria-label="Close crisis resources"
              title="Close"
            >
              x
            </button>

            <div className="bg-gradient-to-br from-[#004f79] via-[#006298] to-[#2a8bc7] px-7 py-6 pr-16 text-white">
              <div className="text-xs font-extrabold tracking-[0.14em] uppercase opacity-90">VICA</div>
              <h2 id="crisis-support-title" className="m-0 mt-2 text-2xl font-extrabold">
                Immediate support resources
              </h2>
            </div>

            <div className="px-7 py-6 text-[#0f2b3a]">
              {CRISIS_MODAL_CONTENT.intro.map((paragraph) => (
                <p key={paragraph} className="m-0 mb-4 text-[15px] leading-7 text-[#1c3f52]">
                  {paragraph}
                </p>
              ))}

              <h3 className="m-0 mt-5 mb-3 text-lg font-extrabold text-[#004f79]">Immediate Resources</h3>
              <div className="space-y-3">
                {CRISIS_MODAL_CONTENT.resources.map((resource) => (
                  <div
                    key={resource.label}
                    className="rounded-xl border border-[#dceaf2] bg-[#f5fbff] px-4 py-3 text-[15px] leading-6 text-[#12384b]"
                  >
                    <span className="font-bold">{resource.label}:</span> {resource.action}{' '}
                    <a
                      className="font-extrabold text-[#006298] underline underline-offset-2"
                      href={getPhoneHref(resource.number)}
                    >
                      {resource.number}
                    </a>
                    {resource.secondaryNumber && (
                      <>
                        {' '}
                        {resource.secondaryAction}{' '}
                        <a
                          className="font-extrabold text-[#006298] underline underline-offset-2"
                          href={getPhoneHref(resource.secondaryNumber)}
                        >
                          {resource.secondaryNumber}
                        </a>
                      </>
                    )}
                    .
                  </div>
                ))}
              </div>

              <h3 className="m-0 mt-6 mb-3 text-lg font-extrabold text-[#004f79]">What to do right now</h3>
              <div className="space-y-3">
                {CRISIS_MODAL_CONTENT.nextSteps.map((step) => (
                  <div
                    key={step}
                    className="rounded-xl border border-[#dceaf2] bg-white px-4 py-3 text-[15px] leading-6 text-[#12384b]"
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Agent;
